import React from 'react';
import { Icon, ListItem, ListItemText } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { FuseUtils } from '@fuse';
import { withRouter } from 'react-router-dom';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import * as Actions from 'app/store/actions';
import FuseNavBadge from './../FuseNavBadge';
import { useIntl } from 'react-intl';
import { localizedItem } from '../localizedMenu';
import { useIsInLimboPeriod, useIsPlacePendingActivateHasAccess } from 'app/auth/authHooks';
import { useIsPlaceRestricted } from 'commons/useIsPlaceRestricted';
import { openDialog } from 'app/auth/store/actions/createNotification.actions';
import { useNotifications } from 'app/main/messages/notificationTimeline/useNotifications';
import * as R from 'ramda';
import { ACTION_NEW, GENERAL_DIALOG } from 'app/constants/compose';

const useStyles = makeStyles(theme => ({
  item: {
    height: 40,
    borderRadius: '8px',
    backgroundColor: theme.palette.green['500'],
    '&:hover': {
      backgroundColor: theme.palette.green['300'],
    },
    '&.active': {
      backgroundColor: theme.palette.green['500'],
      color: theme.palette.secondary.contrastText + '!important',
      pointerEvents: 'none',
      transition: 'border-radius .15s cubic-bezier(0.4,0.0,0.2,1)',
      '& img': {
        backgroundColor: theme.palette.secondary.contrastText,
      },
      '& .list-item-text-primary': {
        color: 'inherit',
      },
      '& .list-item-icon': {
        color: 'inherit',
      },
    },
    '&.active .custom-icon': {
      backgroundColor: 'transparent',
      filter:
        'invert(100%) sepia(0%) saturate(0%) hue-rotate(299deg) brightness(200%) contrast(101%);',
    },
    '& .custom-icon': {
      backgroundColor: 'inherit',
    },
    '&.square, &.active.square': {
      width: '100%',
      borderRadius: '0',
    },
    '& .list-item-icon': {},
    '& .list-item-text': {},
    color: 'white',
    cursor: 'pointer',
    textDecoration: 'none!important',
  },
  mainColorIcon: {
    color: 'white',
  },
}));

function SendMessageButton(props) {
  const dispatch = useDispatch();
  const userRole = useSelector(({ auth }) => auth.user.role);
  const isInLimboPeriod = useIsInLimboPeriod();
  const isPlaceRestricted = useIsPlaceRestricted();
  const navbar = useSelector(({ fuse }) => fuse.navbar);

  const [{ refetchNotifications }] = useNotifications();

  const onOpenCreateNotificationDialog = () => {
    dispatch(
      openDialog({ 
        initialValues:undefined,      
        onCreateSuccess: refetchNotifications,
        type:GENERAL_DIALOG,
        action:ACTION_NEW
      })
    );
  };

  const classes = useStyles(props);
  const { item = {}, active } = props;

  const { formatMessage } = useIntl();
  const isPendingActiveMenu = useIsPlacePendingActivateHasAccess();

  if (!FuseUtils.hasPermission(item.auth, userRole) || R.prop('hidden', item)) {
    return null;
  }

  const buttonWidth = navbar.foldedOpen || navbar.mobileOpen ? 'w-auto' : 'w-full';
  return (
    <div className="sendMessageMenu flex justify-center px-40 py-4">
      <ListItem
        button
        component="button"
        className={clsx(
          classes.item,
          buttonWidth,
          'list-item',
          active,
          'subMenuSidebarMenu',
          'sendMessageButton'
        )}
        disabled={isPendingActiveMenu || isInLimboPeriod || isPlaceRestricted}
        onClick={() => {
          dispatch(Actions.navbarCloseMobile());
          onOpenCreateNotificationDialog();
        }}
        exact={item.exact}
      >
        {item.icon && (
          <Icon
            className={clsx(
              classes.mainColorIcon,
              'list-item-icon text-16 flex-shrink-0 ltr:mr-16 rtl:ml-16'
            )}
          >
            {item.icon}
          </Icon>
        )}
        {item.iconImage && (
          <img
            alt="logo"
            className={clsx(classes.mainColorIcon, 'list-item-img text-16 flex-shrink-0 ltr:mr-16')}
            src={item.iconImage}
          />
        )}
        <div className="w-16 h-16 fill-current text-white list-item-img text-16 flex-shrink-0 ltr:mr-16 custom-icon">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 16 16">
            <g
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeMiterlimit="10"
              strokeWidth="1.5"
              clipPath="url(#a)"
            >
              <path d="M7.642 1.626H5.09A3.335 3.335 0 0 0 1.672 5.26v5.746A3.332 3.332 0 0 0 5.09 14.64h6.026a3.331 3.331 0 0 0 3.417-3.634v-2.78" />
              <path d="m5.824 7.245 5.1-5.164a1.616 1.616 0 0 1 2.3 0l.831.84a1.661 1.661 0 0 1 0 2.33l-5.124 5.19a1.473 1.473 0 0 1-1.048.44H5.324l.064-2.613c.01-.384.166-.75.436-1.023v0ZM10.15 2.879l3.118 3.155" />
            </g>
            <defs>
              <clipPath id="a">
                <path fill="currentColor" d="M0 0h16v16H0z" />
              </clipPath>
            </defs>
          </svg>
        </div>
        <ListItemText
          className="list-item-text"
          primary={localizedItem(item, formatMessage)}
          classes={{ primary: 'font-medium text-14 list-item-text-primary' }}
        />
        {item.badge && <FuseNavBadge badge={item.badge} />}
      </ListItem>
    </div>
  );
}

SendMessageButton.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string,
    icon: PropTypes.string,
    localizedId: PropTypes.string,
  }),
};

export default withRouter(React.memo(SendMessageButton));
